/**
 * homeLanding.js
 *
 * Shared model for the "Main screen after login" (Kezdőképernyő) preference —
 * the destination the app opens on right after sign-in / relaunch. A landing is
 * the combination of:
 *   • an app MODE (`userAppMode`: manager / restaurant / sales / service / stock / driver), and
 *   • an optional TAB inside that module's home. Only the Management module
 *     currently has pinnable tabs (Dashboard / Finance / Calendar / Mailbox / …),
 *     so a manager can land straight on the Calendar or the Mailbox.
 *
 * The preference reuses the SAME two durable settings the login flow already
 * reads, so NO navigation / login changes are needed:
 *   • `userAppMode`        → which module home StartScreen / AppNavigator route to.
 *   • `homeTab:management`  → which Management tab opens first (see useLastTab +
 *     ManagementHomeScreen `preferPinned`).
 *
 * Both the POS settings ({@link module:screens/SettingsScreen}) and the
 * Management settings ({@link module:screens/ManagementSettingsScreen}) build
 * their picker from {@link getHomeLandingOptions} and apply a choice through
 * {@link applyHomeLanding}, keeping the two screens perfectly in sync (this
 * mirrors how {@link module:config/managementTabs} is shared by the tab bar and
 * the picker).
 */

import Observables from '@happy-gastro/global-observables';
import {getSetting, setSetting} from './persistentSettings';
import {getHomeTab, setHomeTab} from './useLastTab';
import {filterModesForAppVariant} from '../config/appVariant';
import {getSwitchableAppModesForUser} from './managerRoles';
import {MANAGEMENT_TABS, visibleManagementTabs} from '../config/managementTabs';

/**
 * navKey (per {@link module:helpers/useLastTab}) for each mode that exposes
 * pinnable home tabs. Only Management is a bottom-tab navigator today — POS,
 * Sales, Service and Warehouse each open a single home screen, so they are
 * deliberately absent here (`MODE_NAV_KEY[mode] || null` yields no pinned tab).
 * @type {Record<string, string>}
 */
export const MODE_NAV_KEY = {manager: 'management'};

/**
 * Per-mode metadata for the top-level module landings. `labelKey`s reuse the
 * already-translated `module_switch_*` strings (present in all 6 locales), so no
 * new translations are required for the module names. `icon` is a
 * MaterialCommunityIcons glyph (Management screen / SafeIcon); `feather` is the
 * Feather equivalent (POS SettingsUiKit).
 */
export const LANDING_MODE_META = {
    manager: {labelKey: 'module_switch_management', fallback: 'Management app', icon: 'view-dashboard-outline', feather: 'bar-chart-2', tone: 'violet'},
    restaurant: {labelKey: 'module_switch_pos', fallback: 'POS értékesítés', icon: 'point-of-sale', feather: 'shopping-bag', tone: 'success'},
    sales: {labelKey: 'module_switch_sales', fallback: 'Értékesítő app', icon: 'account-tie-outline', feather: 'briefcase', tone: 'primary'},
    service: {labelKey: 'module_switch_service', fallback: 'Szerviz modul', icon: 'tools', feather: 'tool', tone: 'info'},
    stock: {labelKey: 'module_switch_warehouse', fallback: 'Raktáros app', icon: 'package-variant-closed', feather: 'package', tone: 'warning'},
    driver: {labelKey: 'module_switch_driver', fallback: 'Sofőr / Futár app', icon: 'truck-delivery-outline', feather: 'truck', tone: 'danger'},
};

/**
 * Feather-icon fallbacks for the Management sub-tab landings (their metadata
 * carries MaterialCommunityIcons glyphs, which the Feather-based POS kit cannot
 * render). Keyed by route name.
 * @type {Record<string, string>}
 */
const TAB_FEATHER_ICON = {
    ManagementDashboard: 'bar-chart-2',
    ManagementData: 'grid',
    ManagementFinance: 'dollar-sign',
    ManagementService: 'tool',
    ManagementCalendar: 'calendar',
    ManagementSettings: 'settings',
    ManagementMail: 'mail',
};

/**
 * When the caller cannot resolve Management access (e.g. the POS settings screen
 * has no `canAccess`), only these always-safe Management screens are offered as
 * landing sub-options. Every manager can reach them regardless of licence.
 * @type {string[]}
 */
const SAFE_MANAGER_LANDING_TABS = ['ManagementDashboard', 'ManagementCalendar', 'ManagementMail'];

/**
 * @typedef {object} HomeLandingOption
 * @property {string} id        Stable id — the persisted landing identity (`<mode>` or `manager:<tab>`).
 * @property {('manager'|'restaurant'|'sales'|'service'|'stock'|'driver')} mode  The app mode this landing selects.
 * @property {?string} navKey   useLastTab navKey when the mode has pinnable tabs (else null).
 * @property {?string} tab      Route name of the pinned home tab (null = module default / last used).
 * @property {string} labelKey  i18n key for the label.
 * @property {string} fallback  Hungarian fallback label.
 * @property {string} icon      MaterialCommunityIcons glyph (Management kit).
 * @property {string} feather   Feather glyph (POS kit).
 * @property {string} tone      Accent tone hint.
 * @property {boolean} sameMode Whether this landing stays in the currently active mode.
 */

/**
 * Build the ordered list of landing destinations the user may pick as their
 * "Main screen after login". Includes every app mode the user is entitled to
 * (honouring role + build-variant), and — for Management — an "Auto (last used)"
 * entry plus one entry per visible Management tab (so Calendar and Mailbox can be
 * a landing screen).
 *
 * @param {object} params
 * @param {object} params.user             Authenticated user document.
 * @param {object} [params.place]          Active restaurant place (place-scoped roles).
 * @param {Function} [params.canAccess]    `useManagementAccess().canAccess`; when omitted a
 *   safe subset of Management screens is offered (Dashboard / Calendar / Mailbox).
 * @param {boolean} [params.mailConfigured] Whether a mailbox is configured (Mail landing gate).
 * @param {string} [params.currentMode]    The currently active `userAppMode` (for `sameMode`).
 * @returns {HomeLandingOption[]}
 */
export const getHomeLandingOptions = ({user, place, canAccess, mailConfigured, currentMode} = {}) => {
    const modes = filterModesForAppVariant(getSwitchableAppModesForUser(user, place));
    const options = [];

    modes.forEach((mode) => {
        if (mode === 'manager') {
            // "Auto (last used)" — clears the pin, opens whatever tab was left open.
            options.push({
                id: 'manager',
                mode: 'manager',
                navKey: 'management',
                tab: null,
                labelKey: 'mgmt_settings_home_auto',
                fallback: 'Automatikus (utoljára használt)',
                icon: 'clock-outline',
                feather: 'clock',
                tone: 'violet',
                sameMode: currentMode === 'manager',
            });

            const tabs = typeof canAccess === 'function'
                ? visibleManagementTabs({canAccess, mailConfigured})
                : MANAGEMENT_TABS.filter((tab) => SAFE_MANAGER_LANDING_TABS.includes(tab.name) && (!tab.mailGated || mailConfigured));

            tabs.forEach((tab) => {
                options.push({
                    id: `manager:${tab.name}`,
                    mode: 'manager',
                    navKey: 'management',
                    tab: tab.name,
                    labelKey: tab.labelKey,
                    fallback: tab.fallback,
                    icon: tab.icon,
                    feather: TAB_FEATHER_ICON[tab.name] || 'square',
                    tone: 'violet',
                    sameMode: currentMode === 'manager',
                });
            });

            return;
        }

        const meta = LANDING_MODE_META[mode] || LANDING_MODE_META.restaurant;
        options.push({
            id: mode,
            mode,
            navKey: MODE_NAV_KEY[mode] || null,
            tab: null,
            labelKey: meta.labelKey,
            fallback: meta.fallback,
            icon: meta.icon,
            feather: meta.feather,
            tone: meta.tone,
            sameMode: currentMode === mode,
        });
    });

    return options;
};

/**
 * Resolve the id of the landing that is currently in effect, so the picker can
 * highlight the active choice. Reads the persisted `userAppMode` (+ the pinned
 * Management tab). A manager with no pinned tab maps to the "Auto" option.
 * @returns {Promise<string>}
 */
export const getCurrentHomeLandingId = async () => {
    const mode = (await getSetting('userAppMode')) || 'restaurant';
    if (mode === 'manager') {
        const tab = await getHomeTab('management');

        return tab ? `manager:${tab}` : 'manager';
    }

    return mode;
};

/**
 * Persist the chosen landing and, when it selects a DIFFERENT module than the one
 * currently active, reload the app so the switch takes effect immediately (this
 * is how the user "switches back" from POS to Management). Choosing a screen
 * inside the current module only pins the tab — it applies on the module's next
 * open, without a disruptive reload.
 *
 * The switch always uses the `restartApp` full root remount (index.js `Root` →
 * remounts `<App/>`) on EVERY platform — iOS, Android, Electron and web. That
 * replays the cold-boot path: App.jsx loadRuntimeState() re-reads the freshly
 * persisted `userAppMode`, re-resolves the mode flags and rebuilds the navigator
 * on the chosen module's home. Crucially, the remount also tears down and
 * re-creates every provider mounted ABOVE the navigator (ManagementDataProvider,
 * MailProvider, …), so a switch can never keep serving the previous module's
 * cached scope/data — the exact "changed mode but nothing reloaded" symptom.
 *
 * `restartApp` is a React remount, NOT an OS process restart, so it is available
 * on iOS too (the old iOS-only soft `reloadAppMode` path left those providers
 * mounted and made the switch feel like a no-op). Any transient iOS teardown
 * hiccup during the remount is absorbed by ErrorFallback's bounded auto-recovery.
 *
 * @param {HomeLandingOption} option  The landing to apply.
 * @returns {Promise<{switched: boolean}>} `switched` is true when the active module changed (app reloaded).
 */
export const applyHomeLanding = async (option) => {
    if (!option || !option.mode) {
        return {switched: false};
    }

    const previousMode = (await getSetting('userAppMode')) || 'restaurant';
    await setSetting('userAppMode', option.mode);

    if (option.navKey) {
        await setHomeTab(option.navKey, option.tab || null);
    }

    const switched = previousMode !== option.mode;
    if (switched) {
        Observables.call('restartApp');
    }

    return {switched};
};

export default getHomeLandingOptions;
